import type { NotificationSound } from "@bookdental/schemas";

/**
 * Síntese de sons curtos via Web Audio API — sem assets externos.
 * Cada som é um pequeno envelope ADSR em 1-2 osciladores.
 *
 * Usa um AudioContext lazy + reutilizado. Browsers modernos exigem
 * user gesture pra criar/resume o contexto — chamar play() dentro de
 * um event handler (click, key) funciona; em polling/auto pode falhar
 * silenciosamente (ok, só não toca).
 */

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const AC = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!AC) return null;
    try {
      ctx = new AC();
    } catch {
      return null;
    }
  }
  if (ctx.state === "suspended") {
    ctx.resume().catch(() => {});
  }
  return ctx;
}

export const SOUND_OPTIONS: Array<{
  key: NotificationSound;
  label: string;
  description: string;
}> = [
  { key: "none", label: "Sem som", description: "Silêncio total" },
  { key: "pop", label: "Pop", description: "Curto e suave" },
  { key: "chime", label: "Chime", description: "Campânula cristalina" },
  { key: "ding", label: "Ding", description: "Sino agudo rápido" },
  { key: "bell", label: "Bell", description: "Sino profundo" },
  { key: "soft", label: "Soft", description: "Tom neutro discreto" },
];

type Note = {
  freq: number;
  start: number; // delay em segundos a partir de now()
  dur: number;
  type?: OscillatorType;
  gain?: number;
};

const SOUNDS: Record<Exclude<NotificationSound, "none">, Note[]> = {
  pop: [{ freq: 880, start: 0, dur: 0.08, type: "sine", gain: 0.22 }],
  chime: [
    { freq: 1046.5, start: 0, dur: 0.35, type: "sine", gain: 0.2 }, // C6
    { freq: 1318.5, start: 0.08, dur: 0.35, type: "sine", gain: 0.17 }, // E6
    { freq: 1568.0, start: 0.16, dur: 0.4, type: "sine", gain: 0.15 }, // G6
  ],
  ding: [
    { freq: 1760, start: 0, dur: 0.22, type: "sine", gain: 0.22 }, // A6
    { freq: 2093, start: 0.03, dur: 0.2, type: "sine", gain: 0.18 }, // C7
  ],
  bell: [
    { freq: 523.25, start: 0, dur: 0.6, type: "sine", gain: 0.25 }, // C5
    { freq: 659.25, start: 0.05, dur: 0.55, type: "sine", gain: 0.18 }, // E5
    { freq: 783.99, start: 0.1, dur: 0.5, type: "triangle", gain: 0.12 }, // G5
  ],
  soft: [{ freq: 440, start: 0, dur: 0.18, type: "triangle", gain: 0.16 }],
};

/** Toca um dos sons. Retorna true se tocou, false se silencioso/sem suporte. */
export function playNotificationSound(sound: NotificationSound): boolean {
  if (sound === "none") return false;
  const notes = SOUNDS[sound];
  if (!notes) return false;
  const audio = getCtx();
  if (!audio) return false;
  const now = audio.currentTime;
  for (const n of notes) {
    const osc = audio.createOscillator();
    const g = audio.createGain();
    osc.type = n.type ?? "sine";
    osc.frequency.value = n.freq;
    const peak = n.gain ?? 0.2;
    // Envelope ADSR simples pra evitar clicks
    g.gain.setValueAtTime(0, now + n.start);
    g.gain.linearRampToValueAtTime(peak, now + n.start + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + n.start + n.dur);
    osc.connect(g);
    g.connect(audio.destination);
    osc.start(now + n.start);
    osc.stop(now + n.start + n.dur + 0.02);
  }
  return true;
}
